import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGlobalDataRefresh from "hooks/useGlobalDataRefresh";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import curved0 from "assets/images/curved-images/curved0.jpg";

import { useAuth } from "context/AuthContext";
import {
  getGuestMissionById,
  guestAirports,
  guestNonprofits,
} from "data/guestDemoData";
import {
  getMissionById,
  getNonProfits,
  getAirports,
  getUsers,
  USERS_API_PAGE_SIZE,
  getActions,
  getTimeline,
  getMissionDocuments,
  addMissionAction,
  saveMission,
} from "./Users/services/graphqlService";
import {
  canEditMissionForProfile,
  isInactiveMissionStatus,
} from "./missionOwnerUtils";
import {
  applyMissionDerivedStatus,
} from "./missionSourcingStatus";
import { formatMissionEndpoint, normalizeMissionRecord, getMissionVisibility } from "./missionLocationUtils";
import MissionDocumentUploadDialog from "components/MissionDocumentUploadDialog";
import MissionDocumentViewerDialog from "components/MissionDocumentViewerDialog";
import DocumentCard from "components/DocumentCard";
import {
  parseLinkedDocumentsFromTimeline,
  parseMissionDocumentsResponse,
  mergeDocumentsWithTimelineMetadata,
  downloadMissionDocument,
  getMissionDocumentSignedUrl,
  parseMissionImageRef,
} from "utils/missionDocuments";

function normalizeNonprofitsList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.NonProfitsList)) return data.NonProfitsList;
  if (Array.isArray(data?.nonprofits)) return data.nonprofits;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function normalizeAirportsList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.AirportsList)) return data.AirportsList;
  if (Array.isArray(data?.airports)) return data.airports;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function normalizeUsersList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.ProfileList)) return data.ProfileList;
  if (Array.isArray(data?.UsersList)) return data.UsersList;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function parseMissionFromResponse(data) {
  if (!data) return null;
  let parsed = typeof data === "string" ? JSON.parse(data) : data;
  if (parsed && !parsed.missionId && !parsed.title) {
    const list =
      parsed.MissionsList ||
      parsed.missions ||
      parsed.items ||
      (Array.isArray(parsed) ? parsed : null);
    if (Array.isArray(list) && list.length > 0) parsed = list[0];
  }
  const looksLikeMission =
    parsed &&
    typeof parsed === "object" &&
    (parsed.missionId || parsed.id || parsed.title);
  if (!looksLikeMission) return null;
  return applyMissionDerivedStatus(normalizeMissionRecord(parsed));
}

function getNonprofitId(np) {
  return np?.nonprofitId ?? np?.id ?? "";
}

const capitalize = (value = "") =>
  value.charAt(0).toUpperCase() + value.slice(1);

const prettify = (value) =>
  value
    ? value
        .toString()
        .replace(/[_-]/g, " ")
        .split(" ")
        .map((chunk) => capitalize(chunk))
        .join(" ")
    : "—";

function formatProposedDateTime(value) {
  if (value == null || String(value).trim() === "") return "";
  const raw = String(value).trim();
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getLegProposedDateTime(leg, mission) {
  return (
    leg?.dateTime ||
    leg?.proposedDateTime ||
    leg?.departureDateTime ||
    mission?.dateTime ||
    ""
  );
}

const MISSION_WORKFLOW_STAGES = [
  { id: "new", label: "New" },
  { id: "sourcing", label: "Sourcing" },
  { id: "ready", label: "Mission Ready" },
  { id: "inflight", label: "Mission Inflight" },
  { id: "completed", label: "Mission Completed" },
];

function getWorkflowStepIndex(mission) {
  if (!mission) return 0;
  const status = (mission.status || "").toLowerCase();
  const pilot = (mission.pilotStatus || "").toLowerCase();
  const cargo = (mission.cargoStatus || "").toLowerCase();

  const isCompleted =
    status.includes("completed") ||
    status.includes("complete") ||
    status.includes("done");
  if (isCompleted) return 4;
  if (status.includes("departed")) return 3;
  if (status.includes("ready")) return 2;

  const pilotReady = pilot.includes("chosen") || pilot.includes("sourced");
  const cargoReady = cargo.includes("sourced");

  const isReady = pilotReady && cargoReady;
  const isInflight =
    (status.includes("inflight") || status.includes("in-flight")) && isReady;

  if (isInflight) return 3;
  if (isReady) return 2;
  if (
    pilot.includes("needed") ||
    pilot.includes("pending") ||
    cargo.includes("needed") ||
    cargo.includes("pending") ||
    status.includes("processing") ||
    status.includes("progress") ||
    status.includes("pending") ||
    status.includes("active") ||
    status.includes("draft")
  )
    return 1;
  return 0;
}

function MissionStatusBar({ mission }) {
  const currentStep = getWorkflowStepIndex(mission);

  return (
    <SoftBox
      mb={3}
      p={2.5}
      sx={(theme) => ({
        borderRadius: "1rem",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 4px 20px rgba(15, 34, 58, 0.08)",
      })}
    >
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={{ xs: 0.5, sm: 1 }}
      >
        {MISSION_WORKFLOW_STAGES.map((stage, index) => (
          <SoftBox
            key={stage.id}
            display="flex"
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <SoftBox
              px={{ xs: 1.5, sm: 2 }}
              py={1}
              sx={(theme) => {
                const isDoneOrCurrent = index <= currentStep;
                return {
                  borderRadius: "0.5rem",
                  fontWeight: 700,
                  fontSize: theme.typography.pxToRem(13),
                  backgroundColor: isDoneOrCurrent
                    ? theme.palette.info.main
                    : theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                  color: isDoneOrCurrent
                    ? theme.palette.common.white
                    : theme.palette.text.secondary,
                  transition: "all 0.2s ease",
                };
              }}
            >
              {stage.label}
            </SoftBox>
            {index < MISSION_WORKFLOW_STAGES.length - 1 && (
              <Icon
                sx={{
                  fontSize: "1.25rem",
                  color: "divider",
                  mx: { xs: 0.25, sm: 0.5 },
                }}
              >
                arrow_forward
              </Icon>
            )}
          </SoftBox>
        ))}
      </SoftBox>
    </SoftBox>
  );
}

function MissionOverviewHeader({ mission, idToken }) {
  const visibility = getMissionVisibility(mission);
  const isInternal = visibility === "Internal";
  const subtitle =
    [mission.missionCategory].filter(Boolean).map((v) => prettify(v)).join(" • ") ||
    "Mission";

  const imageRef = useMemo(
    () => parseMissionImageRef(mission?.image),
    [mission?.image],
  );
  const [headerImageUrl, setHeaderImageUrl] = useState(null);

  useEffect(() => {
    if (!imageRef?.fileName || !idToken) {
      setHeaderImageUrl(null);
      return undefined;
    }

    let cancelled = false;
    getMissionDocumentSignedUrl(
      {
        fileName: imageRef.fileName,
        bucket: imageRef.bucket || "info.avipair.com",
        key: imageRef.fileName,
      },
      idToken,
    )
      .then((url) => {
        if (!cancelled) setHeaderImageUrl(url);
      })
      .catch((err) => {
        console.error("Failed to load mission image:", err);
        if (!cancelled) setHeaderImageUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [imageRef?.fileName, imageRef?.bucket, idToken]);

  const heroImage = headerImageUrl || curved0;

  return (
    <SoftBox position="relative">
      <SoftBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({
            functions: { rgba, linearGradient },
            palette: { gradients },
          }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6),
            )}, url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />
      <Card
        sx={{
          backdropFilter: "saturate(200%) blur(30px)",
          backgroundColor: ({ functions: { rgba }, palette: { white } }) =>
            rgba(white.main, 0.8),
          boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <SoftBox lineHeight={1}>
              <SoftBox
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={0.5}
              >
                <SoftTypography variant="h5" fontWeight="medium">
                  {mission.title || "Untitled Mission"}
                </SoftTypography>
                <SoftBadge
                  variant="gradient"
                  color={isInternal ? "warning" : "success"}
                  size="xs"
                  badgeContent={isInternal ? "Internal" : "Public"}
                  container
                />
              </SoftBox>
              <SoftTypography variant="button" color="text" fontWeight="medium">
                {subtitle}
              </SoftTypography>
              {isInternal ? (
                <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
                  Internal missions are intended for onboarded pilots on this
                  organization&apos;s team, not the public board.
                </SoftTypography>
              ) : null}
              {mission.description ? (
                <SoftTypography
                  variant="body2"
                  color="text"
                  mt={1}
                  maxWidth={720}
                >
                  {mission.description}
                </SoftTypography>
              ) : null}
            </SoftBox>
          </Grid>
        </Grid>
      </Card>
    </SoftBox>
  );
}

function buildMissionTimeline(mission) {
  if (!mission) return [];

  const timeline = [];

  timeline.push({
    title: "Mission created",
    detail: mission.postedBy
      ? `Posted by ${mission.postedBy}`
      : "Mission record created",
    meta: mission.created || "Initial state",
    color: "info",
    icon: "add_circle",
  });

  if (mission.dateTime) {
    timeline.push({
      title: "Scheduled",
      detail: formatProposedDateTime(mission.dateTime) || mission.dateTime,
      meta: "Proposed departure date/time",
      color: "primary",
      icon: "event",
    });
  }

  const routeDep = formatMissionEndpoint(
    mission.departure,
    mission.departureCity,
    mission.departureState,
  );
  const routeArr = formatMissionEndpoint(
    mission.arrival,
    mission.arrivalCity,
    mission.arrivalState,
  );
  if (routeDep || routeArr) {
    timeline.push({
      title: "Route set",
      detail: `${routeDep || "—"} → ${routeArr || "—"}`,
      meta: "Flight route confirmed",
      color: "success",
      icon: "alt_route",
    });
  }

  if (mission.pilotStatus) {
    timeline.push({
      title: "Pilot status updated",
      detail: prettify(mission.pilotStatus),
      meta: "Crew assignment progress",
      color: "secondary",
      icon: "person",
    });
  }

  if (mission.cargoStatus) {
    timeline.push({
      title: "Cargo status updated",
      detail: prettify(mission.cargoStatus),
      meta: "Cargo sourcing progress",
      color: "warning",
      icon: "inventory_2",
    });
  }

  if (mission.linkedRequestId) {
    timeline.push({
      title: "Linked resource request",
      detail: String(mission.linkedRequestId),
      meta: "Request relationship",
      color: "dark",
      icon: "link",
    });
  }

  if (mission.updated) {
    timeline.push({
      title: "Last updated",
      detail: mission.updated,
      meta: "Most recent mission change",
      color: "info",
      icon: "update",
    });
  }

  return timeline;
}

function parseTimelinePayload(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return parseTimelinePayload(JSON.parse(data));
    } catch {
      return [];
    }
  }
  if (typeof data !== "object") return [];

  const arrayKeys = [
    "Timeline",
    "timeline",
    "TimelineList",
    "timelines",
    "items",
    "list",
    "results",
    "records",
    "content",
    "rows",
  ];
  for (const key of arrayKeys) {
    if (Array.isArray(data[key])) return data[key];
  }
  if (data.timelineId || data.message || data.type) {
    return [data];
  }
  return [];
}

function isCompleteMissionAction(action) {
  const key = (action?.type ?? action?.subType ?? "").toLowerCase();
  return key.includes("completemission");
}

function isMissionStatusAction(action) {
  const key = `${action?.type ?? ""} ${action?.subType ?? ""}`.toLowerCase();
  return (
    key.includes("missionready") ||
    key.includes("missiondeparted") ||
    key.includes("departed")
  );
}

function isChangeOwnerAction(action) {
  const key = `${action?.type ?? ""} ${action?.subType ?? ""}`.toLowerCase();
  return key.includes("changeowner");
}

function isOfferingCargoAction(action) {
  const key = `${action?.type ?? ""} ${action?.subType ?? ""}`.toLowerCase();
  return key.includes("offeringcargo");
}

function getProfileUserId(u) {
  return String(u?.profileId ?? u?.id ?? "").trim();
}

function getProfileUserLabel(u) {
  const name = (u?.name || "").trim();
  const email = (u?.email || "").trim();
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  const id = getProfileUserId(u);
  return id ? `User ${id}` : "User";
}

function getUserOrgIds(u) {
  const list = u?.association ?? u?.associations ?? u?.profile?.association ?? [];
  if (!Array.isArray(list)) return [];
  return list
    .flatMap((a) => [
      a?.entityId,
      a?.EntityId,
      a?.nonprofitId,
      a?.orgId,
    ])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);
}

function looksLikeEntityId(value) {
  const s = String(value || "").trim();
  if (!s || s.includes(" ") || s.includes("@")) return false;
  return s.length >= 8;
}

function getMissionOrganizationId(mission, tokenEntityId) {
  const fromOwner = String(
    mission?.owner?.entityId || mission?.ownerEntityId || "",
  ).trim();
  if (fromOwner) return fromOwner;
  const pocOrg = String(mission?.missionPOCOrgId || "").trim();
  if (pocOrg) return pocOrg;
  const postedBy = String(mission?.postedBy || "").trim();
  if (looksLikeEntityId(postedBy)) return postedBy;
  return String(tokenEntityId || "").trim();
}

async function fetchAllUserPages(idToken) {
  const collected = [];
  let nextToken = null;
  for (let page = 0; page < 50; page += 1) {
    const result = await getUsers(idToken, USERS_API_PAGE_SIZE, nextToken);
    const list = Array.isArray(result?.data) ? result.data : [];
    collected.push(...list);
    if (!result?.hasMore || !result?.nextToken) break;
    nextToken = result.nextToken;
  }
  return collected;
}

const formatTimelineDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

function timelineTypeStyle(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("document")) return { icon: "description", color: "info" };
  if (t.includes("status")) return { icon: "flag", color: "warning" };
  if (t.includes("pilot")) return { icon: "person", color: "primary" };
  if (t.includes("cargo")) return { icon: "inventory_2", color: "warning" };
  if (t.includes("mission")) return { icon: "flight", color: "success" };
  return { icon: "history", color: "secondary" };
}

/** Map getTimeline events to existing timeline card shape; sort oldest → newest. */
function mapApiTimelineToUi(events) {
  if (!Array.isArray(events) || events.length === 0) return [];

  return [...events]
    .sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    })
    .map((ev) => {
      const { icon, color } = timelineTypeStyle(ev.type);
      const title = ev.type ? prettify(ev.type) : "Update";
      const detail = ev.message || ev.messageTemplate || "—";
      const metaParts = [formatTimelineDate(ev.createdAt)];
      if (ev.loggedProfileName) metaParts.push(ev.loggedProfileName);
      if (Array.isArray(ev.roles) && ev.roles.length) {
        metaParts.push(ev.roles.join(", "));
      }
      return {
        timelineId: ev.timelineId,
        title,
        detail,
        meta: metaParts.filter(Boolean).join(" · "),
        color,
        icon,
      };
    });
}

function MissionDetailsPage() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const {
    user,
    profile,
    tokenRole,
    tokenProfileId,
    tokenEntityId,
    tokenEntityType,
    isGuest,
  } = useAuth();
  const isViewOnlyUser =
    (tokenRole || "").toLowerCase() === "user" || isGuest;
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonprofits, setNonprofits] = useState([]);
  const [airports, setAirports] = useState([]);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [cargoOfferingValues, setCargoOfferingValues] = useState([]);
  const [selectedOwnerProfileId, setSelectedOwnerProfileId] = useState("");
  const [ownerPickerLoading, setOwnerPickerLoading] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [missionDocuments, setMissionDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [documentsFetched, setDocumentsFetched] = useState(false);
  const [viewerDocument, setViewerDocument] = useState(null);
  const [documentActionId, setDocumentActionId] = useState(null);
  const [pendingDeleteMission, setPendingDeleteMission] = useState(false);
  const [deletingMission, setDeletingMission] = useState(false);

  useEffect(() => {
    if (isGuest && missionId) {
      setMission(getGuestMissionById(missionId));
      setLoading(false);
      return undefined;
    }
    if (!missionId || !user?.id_token) {
      setLoading(false);
      setMission(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);

    const loadMission = async () => {
      // Save can return before the new record is readable. Retry briefly.
      const retryDelays = [0, 350, 700, 1400, 2500];

      for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
        if (retryDelays[attempt] > 0) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, retryDelays[attempt]),
          );
        }
        if (cancelled) return;

        try {
          const res = await getMissionById(missionId, user.id_token);
          const parsed = parseMissionFromResponse(res?.data);
          if (!parsed || isInactiveMissionStatus(parsed)) {
            if (attempt < retryDelays.length - 1 && !parsed) continue;
            setMission(null);
            return;
          }
          if (cancelled) return;
          setMission(parsed);
          return;
        } catch (error) {
          const isNotFound = /not found/i.test(String(error?.message || ""));
          if (isNotFound && attempt < retryDelays.length - 1) continue;
          if (!cancelled) setMission(null);
          return;
        }
      }
    };

    loadMission().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [missionId, user?.id_token, isGuest]);

  useEffect(() => {
    if (!isGuest) return;
    setNonprofits(guestNonprofits);
    setAirports(guestAirports);
  }, [isGuest]);

  useEffect(() => {
    if (!user?.id_token) return;
    let cancelled = false;
    getNonProfits(user.id_token)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeNonprofitsList(res?.data);
        setNonprofits(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setNonprofits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_token]);

  const fetchTimeline = useCallback(async () => {
    if (isGuest || !user?.id_token || !missionId) {
      setTimelineEvents([]);
      setTimelineLoading(false);
      return;
    }
    setTimelineLoading(true);
    try {
      const res = await getTimeline(missionId, user.id_token);
      const parsed = parseTimelinePayload(res?.data);
      setTimelineEvents(Array.isArray(parsed) ? parsed : []);
    } catch {
      setTimelineEvents([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [isGuest, missionId, user?.id_token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchTimeline();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchTimeline]);

  const fetchMissionDocuments = useCallback(async () => {
    const resolvedId =
      mission?.missionId ?? mission?.id ?? missionId ?? "";

    if (isGuest || !user?.id_token || !resolvedId) {
      setMissionDocuments([]);
      setDocumentsLoading(false);
      setDocumentsError(null);
      setDocumentsFetched(false);
      return;
    }

    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const res = await getMissionDocuments(resolvedId, user.id_token);
      const docs = parseMissionDocumentsResponse(res?.data ?? res?.raw);
      setMissionDocuments(docs);
      setDocumentsFetched(true);
    } catch (err) {
      setMissionDocuments([]);
      setDocumentsError(err?.message || "Failed to load documents");
      setDocumentsFetched(false);
    } finally {
      setDocumentsLoading(false);
    }
  }, [isGuest, missionId, mission?.missionId, mission?.id, user?.id_token]);

  useEffect(() => {
    setDocumentsFetched(false);
    setMissionDocuments([]);
    setDocumentsError(null);
  }, [missionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchMissionDocuments();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMissionDocuments]);

  const reloadMissionFromServer = useCallback(async () => {
    if (isGuest || !missionId || !user?.id_token) return;
    try {
      const res = await getMissionById(missionId, user.id_token);
      setMission(parseMissionFromResponse(res?.data));
    } catch {
      setMission(null);
    }
  }, [isGuest, missionId, user?.id_token]);

  useGlobalDataRefresh(
    ["mission", "missionid"],
    useCallback(
      (detail) => {
        if (detail?.dataId && String(detail.dataId) !== String(missionId)) {
          return;
        }
        reloadMissionFromServer();
        fetchTimeline();
        fetchMissionDocuments();
      },
      [
        missionId,
        reloadMissionFromServer,
        fetchTimeline,
        fetchMissionDocuments,
      ],
    ),
  );

  useEffect(() => {
    if (!user?.id_token) return;
    let cancelled = false;
    getAirports(user.id_token)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeAirportsList(res?.data);
        setAirports(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAirports([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_token]);

  useEffect(() => {
    if (!user?.id_token) return;
    let cancelled = false;
    getUsers(user.id_token)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeUsersList(res?.data);
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_token]);

  useEffect(() => {
    if (!user?.id_token) return;
    let cancelled = false;
    Promise.all([
      getActions("Mission", user.id_token),
      getActions("ResourceRequest", user.id_token).catch(() => []),
    ])
      .then(([missionActions, requestActions]) => {
        if (cancelled) return;
        const merged = [...(Array.isArray(missionActions) ? missionActions : [])];
        (Array.isArray(requestActions) ? requestActions : [])
          .filter(isCompleteMissionAction)
          .forEach((action) => {
            const exists = merged.some(
              (existing) =>
                (existing.actionId &&
                  action.actionId &&
                  existing.actionId === action.actionId) ||
                (existing.type === action.type &&
                  existing.subType === action.subType),
            );
            if (!exists) merged.push(action);
          });
        setActions(merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      })
      .catch(() => {
        if (!cancelled) setActions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_token]);

  const getAirportId = (a) => a?.airportId ?? a?.id ?? "";
  const getAirportName = (airportId) => {
    if (!airportId) return "—";
    const airport = airports.find((a) => getAirportId(a) === String(airportId));
    return airport?.name || airportId;
  };

  const getUserId = (u) => getProfileUserId(u);
  const missionOrgId = useMemo(
    () => getMissionOrganizationId(mission, tokenEntityId),
    [mission, tokenEntityId],
  );
  const orgOwnerOptions = useMemo(() => {
    if (!missionOrgId) return [];
    return users
      .filter((u) => getUserOrgIds(u).includes(missionOrgId))
      .map((u) => ({
        value: getProfileUserId(u),
        label: getProfileUserLabel(u),
      }))
      .filter((opt) => opt.value);
  }, [users, missionOrgId]);
  const getPilotDisplay = (leg) => {
    const profileId = leg?.pilot?.profileId;
    if (!profileId) return leg?.pilot?.name || "—";
    const found = users.find((u) => getUserId(u) === String(profileId));
    if (!found) return leg?.pilot?.name || profileId;
    const name = found.name || "";
    const email = found.email || "";
    if (name && email) return `${name} (${email})`;
    if (name) return name;
    if (email) return email;
    return profileId;
  };

  const pocOrgName =
    mission?.missionPOCOrgId &&
    nonprofits.find(
      (np) => getNonprofitId(np) === String(mission.missionPOCOrgId),
    )?.name;
  const linkedDocuments = useMemo(() => {
    if (documentsFetched && !isGuest && !documentsError) {
      return mergeDocumentsWithTimelineMetadata(
        missionDocuments,
        timelineEvents,
      );
    }
    if (documentsLoading) return [];
    return parseLinkedDocumentsFromTimeline(timelineEvents);
  }, [
    missionDocuments,
    timelineEvents,
    documentsFetched,
    documentsLoading,
    documentsError,
    isGuest,
  ]);

  const resolvedMissionId =
    mission?.missionId ?? mission?.id ?? missionId ?? "";

  const timeline = useMemo(() => {
    const fromApi = mapApiTimelineToUi(timelineEvents);
    if (fromApi.length > 0) return fromApi;
    if (isGuest) return buildMissionTimeline(mission);
    return [];
  }, [timelineEvents, mission, isGuest]);

  const currentProfileId = profile?.profileId || tokenProfileId;
  const canEditMission =
    mission &&
    canEditMissionForProfile(mission, {
      profileId: currentProfileId,
      isGuest,
      tokenRole,
      tokenEntityType,
    });
  const canDeleteMission = canEditMission;

  const handleDeleteMission = async () => {
    const idToUse = mission?.missionId ?? mission?.id ?? missionId;
    if (!idToUse || !user?.id_token || deletingMission || !canDeleteMission) {
      return;
    }
    setDeletingMission(true);
    try {
      const saveResult = await saveMission(
        { status: "Inactive" },
        user.id_token,
        idToUse,
      );
      if (!saveResult.success) {
        throw new Error(saveResult.message || "Failed to delete mission.");
      }
      setPendingDeleteMission(false);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "success",
            title: "Success",
            message: "Mission deleted successfully.",
          },
        }),
      );
      navigate("/dashboard/missions");
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message: err.message || "Failed to delete mission.",
          },
        }),
      );
    } finally {
      setDeletingMission(false);
    }
  };

  const handleViewDocument = (doc) => {
    if (!user?.id_token || isGuest) return;
    if (!(doc.key || doc.documentPath)) return;
    setViewerDocument(doc);
  };

  const handleDownloadDocument = async (doc) => {
    if (!user?.id_token || isGuest) return;
    const docId = doc.id || doc.timelineId || doc.documentPath;
    setDocumentActionId(docId);
    try {
      await downloadMissionDocument(doc, user.id_token);
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Download failed",
            message: err?.message ?? "Could not download document.",
          },
        }),
      );
    } finally {
      setDocumentActionId(null);
    }
  };

  if (loading) {
    return (
      <SoftBox
        py={3}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={220}
      >
        <CircularProgress />
      </SoftBox>
    );
  }

  if (!mission) {
    return (
      <SoftBox py={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Mission not found.
        </Alert>
        <SoftButton
          variant="text"
          color="dark"
          onClick={() => navigate("/dashboard/missions")}
          startIcon={<Icon>arrow_back</Icon>}
        >
          Back to Missions
        </SoftButton>
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <SoftButton
          variant="text"
          color="dark"
          onClick={() => navigate("/dashboard/missions")}
          startIcon={<Icon>arrow_back</Icon>}
        >
          Back to Missions
        </SoftButton>

        <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {!isGuest && (
            <>
          <SoftButton
            variant="gradient"
            color="info"
            startIcon={<Icon>bolt</Icon>}
            onClick={(e) => setActionMenuAnchorEl(e.currentTarget)}
          >
            Action
          </SoftButton>
          <Menu
            anchorEl={actionMenuAnchorEl}
            open={!!actionMenuAnchorEl}
            onClose={() => setActionMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: { minWidth: 180 },
            }}
          >
            {actions.map((action, idx) => (
              <MenuItem
                key={action.actionId ?? action.type ?? idx}
                onClick={() => {
                  setActionMenuAnchorEl(null);
                  setPendingAction(action);
                  setSelectedOwnerProfileId("");
                  const isOfferingCargo = isOfferingCargoAction(action);
                  if (isOfferingCargo && mission?.cargoItems?.length) {
                    setCargoOfferingValues(
                      mission.cargoItems.map((c) => ({
                        offeredValue:
                          c.offeredValue != null && c.offeredValue !== ""
                            ? String(c.offeredValue)
                            : "",
                      })),
                    );
                  } else {
                    setCargoOfferingValues([]);
                  }
                  if (isChangeOwnerAction(action) && user?.id_token) {
                    setOwnerPickerLoading(true);
                    fetchAllUserPages(user.id_token)
                      .then((list) => setUsers(Array.isArray(list) ? list : []))
                      .catch(() => {})
                      .finally(() => setOwnerPickerLoading(false));
                  }
                }}
              >
                {action.label ?? action.message ?? action.type}
              </MenuItem>
            ))}
            {actions.length === 0 && (
              <MenuItem disabled>No actions available</MenuItem>
            )}
          </Menu>
          <Dialog
            open={!!pendingAction}
            onClose={() => {
              if (!actionSubmitting) {
                setPendingAction(null);
                setCargoOfferingValues([]);
                setSelectedOwnerProfileId("");
              }
            }}
            maxWidth={
              isOfferingCargoAction(pendingAction)
                ? "md"
                : isChangeOwnerAction(pendingAction)
                  ? "sm"
                  : "xs"
            }
            fullWidth
          >
            <DialogTitle>
              {pendingAction?.message ??
                pendingAction?.label ??
                "Confirm action"}
            </DialogTitle>
            <DialogContent>
              {(() => {
                const isOfferingCargo = isOfferingCargoAction(pendingAction);
                const cargoItems = mission?.cargoItems ?? [];

                if (isChangeOwnerAction(pendingAction)) {
                  const orgLabel =
                    (missionOrgId &&
                      nonprofits.find(
                        (np) => getNonprofitId(np) === missionOrgId,
                      )?.name) ||
                    missionOrgId ||
                    "this organization";
                  return (
                    <SoftBox pt={0.5}>
                      <DialogContentText sx={{ mb: 2 }}>
                        Choose the new owner from users in {orgLabel}.
                      </DialogContentText>
                      {ownerPickerLoading ? (
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={1}
                        >
                          <CircularProgress size={18} />
                          <SoftTypography variant="caption" color="text">
                            Loading organization users…
                          </SoftTypography>
                        </SoftBox>
                      ) : null}
                      <SoftSelect
                        placeholder={
                          missionOrgId
                            ? "Select a user"
                            : "No organization found"
                        }
                        options={orgOwnerOptions}
                        value={
                          orgOwnerOptions.find(
                            (opt) => opt.value === selectedOwnerProfileId,
                          ) ?? null
                        }
                        onChange={(opt) =>
                          setSelectedOwnerProfileId(opt?.value ?? "")
                        }
                        isDisabled={
                          ownerPickerLoading ||
                          !missionOrgId ||
                          orgOwnerOptions.length === 0
                        }
                        isClearable
                      />
                      {!ownerPickerLoading && missionOrgId && orgOwnerOptions.length === 0 ? (
                        <SoftTypography
                          variant="caption"
                          color="text"
                          display="block"
                          mt={1}
                        >
                          No users are associated with this organization.
                        </SoftTypography>
                      ) : null}
                      {!missionOrgId ? (
                        <SoftTypography
                          variant="caption"
                          color="error"
                          display="block"
                          mt={1}
                        >
                          This mission has no organization to load users from.
                        </SoftTypography>
                      ) : null}
                    </SoftBox>
                  );
                }

                if (isOfferingCargo) {
                  if (!cargoItems.length) {
                    return (
                      <DialogContentText>
                        No cargo items to offer for this mission.
                      </DialogContentText>
                    );
                  }
                  return (
                    <SoftBox pt={0.5}>
                      <SoftTypography variant="body2" color="text" mb={2}>
                        Fill in the values for each requested cargo item:
                      </SoftTypography>
                      {cargoItems.map((item, idx) => (
                        <SoftBox
                          key={idx}
                          mb={2}
                          p={1.5}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            color="dark"
                          >
                            {item.description || `Item ${idx + 1}`}
                          </SoftTypography>
                          <SoftTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={1}
                          >
                            Requested:{" "}
                            {item.count != null && item.count !== ""
                              ? `${item.count} × `
                              : ""}
                            {item.weightLbs ?? "—"} lbs
                            {item.count != null &&
                            item.count !== "" &&
                            item.weightLbs != null &&
                            item.weightLbs !== "" &&
                            !Number.isNaN(Number(item.count)) &&
                            !Number.isNaN(Number(item.weightLbs))
                              ? ` (total ${Number(item.count) * Number(item.weightLbs)} lbs)`
                              : ""}
                          </SoftTypography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6} sm={4} md={3}>
                              <SoftTypography variant="caption">
                                Offered Value
                              </SoftTypography>
                              <SoftInput
                                type="number"
                                inputProps={{ min: 0 }}
                                size="small"
                                placeholder="Value"
                                value={
                                  cargoOfferingValues[idx]?.offeredValue ?? ""
                                }
                                onChange={(e) => {
                                  const next = [...cargoOfferingValues];
                                  next[idx] = {
                                    ...(next[idx] ?? {}),
                                    offeredValue: e.target.value,
                                  };
                                  setCargoOfferingValues(next);
                                }}
                              />
                            </Grid>
                          </Grid>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  );
                }

                return (
                  <DialogContentText>
                    {isCompleteMissionAction(pendingAction)
                      ? "Mark this mission as complete and departed?"
                      : `Are you sure you want to "${
                          pendingAction?.message ??
                          pendingAction?.label ??
                          pendingAction?.type
                        }"?`}
                  </DialogContentText>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <SoftButton
                variant="text"
                color="secondary"
                onClick={() => {
                  setPendingAction(null);
                  setCargoOfferingValues([]);
                  setSelectedOwnerProfileId("");
                }}
                disabled={actionSubmitting}
              >
                Cancel
              </SoftButton>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={async () => {
                  if (!pendingAction || !user?.id_token || !mission) return;
                  const isOfferingCargoNoItems =
                    isOfferingCargoAction(pendingAction) &&
                    !(mission?.cargoItems?.length > 0);
                  if (isOfferingCargoNoItems) return;
                  if (
                    isChangeOwnerAction(pendingAction) &&
                    !selectedOwnerProfileId
                  ) {
                    return;
                  }
                  const idToUse = mission.missionId ?? mission.id ?? missionId;
                  if (!idToUse) return;
                  setActionSubmitting(true);
                  try {
                    const actionMessage =
                      pendingAction.message ??
                      pendingAction.label ??
                      pendingAction.type;
                    const isOfferingCargo =
                      isOfferingCargoAction(pendingAction);

                    let actionInput = {
                      message: actionMessage,
                      type: pendingAction.type,
                      subType: pendingAction.subType ?? undefined,
                    };

                    const isCompleteMission =
                      isCompleteMissionAction(pendingAction);
                    const isMissionStatus =
                      isMissionStatusAction(pendingAction);
                    if (isCompleteMission) {
                      actionInput.data = [
                        { name: "missionId", value: String(idToUse) },
                      ];
                    }

                    if (isChangeOwnerAction(pendingAction)) {
                      actionInput.data = [
                        {
                          name: "profileId",
                          value: String(selectedOwnerProfileId),
                        },
                        {
                          name: "ownerProfileId",
                          value: String(selectedOwnerProfileId),
                        },
                      ];
                    }

                    let cargoOfferingsForPersist = null;
                    if (
                      !isCompleteMission &&
                      isOfferingCargo &&
                      mission.cargoItems?.length &&
                      cargoOfferingValues.length
                    ) {
                      cargoOfferingsForPersist = mission.cargoItems.map(
                        (item, idx) => {
                          const rawValue =
                            cargoOfferingValues[idx]?.offeredValue;
                          const offeredValue =
                            rawValue != null && rawValue !== ""
                              ? parseInt(rawValue, 10)
                              : undefined;

                          return {
                            description: item.description ?? "",
                            ...(offeredValue !== undefined
                              ? { offeredValue }
                              : {}),
                          };
                        },
                      );
                      actionInput.data = [
                        {
                          name: "cargoOfferings",
                          value: JSON.stringify(cargoOfferingsForPersist),
                        },
                      ];
                    }

                    await addMissionAction(idToUse, actionInput, user.id_token);

                    let successMessage =
                      actionMessage ?? "Action completed successfully.";
                    if (
                      isCompleteMission ||
                      isMissionStatus ||
                      isChangeOwnerAction(pendingAction)
                    ) {
                      try {
                        const refreshed = await getMissionById(
                          idToUse,
                          user.id_token,
                        );
                        const m = parseMissionFromResponse(refreshed?.data);
                        if (m) setMission(m);
                      } catch {
                        // ignore refetch errors
                      }
                    } else if (cargoOfferingsForPersist) {
                      try {
                        const refreshed = await getMissionById(
                          idToUse,
                          user.id_token,
                        );
                        const m = parseMissionFromResponse(refreshed?.data);
                        if (m) setMission(m);
                      } catch {
                        // ignore refetch errors
                      }
                    }

                    setPendingAction(null);
                    setCargoOfferingValues([]);
                    setSelectedOwnerProfileId("");
                    fetchTimeline();
                    window.dispatchEvent(
                      new CustomEvent("globalNotification", {
                        detail: {
                          type: "success",
                          title: "Action submitted",
                          message: successMessage,
                        },
                      }),
                    );
                  } catch (err) {
                    window.dispatchEvent(
                      new CustomEvent("globalNotification", {
                        detail: {
                          type: "error",
                          title: "Action failed",
                          message: err?.message ?? "Failed to submit action.",
                        },
                      }),
                    );
                  } finally {
                    setActionSubmitting(false);
                  }
                }}
                disabled={
                  actionSubmitting ||
                  (isOfferingCargoAction(pendingAction) &&
                    !(mission?.cargoItems?.length > 0)) ||
                  (isChangeOwnerAction(pendingAction) &&
                    (!selectedOwnerProfileId || ownerPickerLoading))
                }
              >
                {actionSubmitting ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    Submitting…
                  </>
                ) : (
                  "OK"
                )}
              </SoftButton>
            </DialogActions>
          </Dialog>
          {canEditMission && (
            <SoftButton
              variant="outlined"
              color="info"
              startIcon={<Icon>edit</Icon>}
              onClick={() =>
                navigate("/dashboard/missions", {
                  state: {
                    editMissionId: mission.missionId || mission.id || missionId,
                  },
                })
              }
            >
              Edit
            </SoftButton>
          )}
          {canDeleteMission && (
            <SoftButton
              variant="outlined"
              color="error"
              startIcon={<Icon>delete_outline</Icon>}
              onClick={() => setPendingDeleteMission(true)}
            >
              Delete
            </SoftButton>
          )}
            </>
          )}
        </SoftBox>
      </SoftBox>

      <MissionOverviewHeader mission={mission} idToken={user?.id_token} />

      <SoftBox mt={5} mb={3}>
        <MissionStatusBar mission={mission} />
      </SoftBox>

      <SoftBox>
        {/* Statistics row - ComplexStatisticsCard (reports style) */}
        <SoftBox pt={2} px={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="dark"
                icon="volunteer_activism"
                iconSize="large"
                count={{
                  number: mission.livesImpacted ?? 0,
                  label: "Lives Impacted",
                }}
                percentage=""
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="dark"
                icon="person"
                iconSize="large"
                count={{
                  number: prettify(mission.pilotStatus) || "—",
                  label: "Pilot Status",
                }}
                percentage=""
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="dark"
                icon="inventory_2"
                iconSize="large"
                count={{
                  number: prettify(mission.cargoStatus) || "—",
                  label: "Cargo Status",
                }}
                percentage=""
              />
            </Grid>
          </Grid>
        </SoftBox>

        <Divider />

        {/* Trip Destination & Legs */}
        <SoftBox pt={2} px={2} pb={2}>
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Trip Destination
          </SoftTypography>
          <SoftBox
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={3}
            p={3}
            mb={3}
            sx={(theme) => ({
              borderRadius: "1rem",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(99, 110, 255, 0.08)",
              border: `1px solid ${theme.palette.divider}`,
              width: "100%",
            })}
          >
            <SoftBox
              display="flex"
              alignItems="center"
              gap={1.5}
              flex="1"
              minWidth={140}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={48}
                height={48}
                borderRadius="md"
                sx={(theme) => ({
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.common.white,
                })}
              >
                <Icon sx={{ fontSize: "1.75rem" }}>flight_takeoff</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                >
                  Departure
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" display="block">
                  {formatMissionEndpoint(
                    mission.departure,
                    mission.departureCity,
                    mission.departureState,
                  ) || "—"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <Icon color="info" sx={{ fontSize: "2rem" }}>
              arrow_forward
            </Icon>
            <SoftBox
              display="flex"
              alignItems="center"
              gap={1.5}
              flex="1"
              minWidth={140}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={48}
                height={48}
                borderRadius="md"
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                })}
              >
                <Icon sx={{ fontSize: "1.75rem" }}>flight_land</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                >
                  Arrival
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" display="block">
                  {formatMissionEndpoint(
                    mission.arrival,
                    mission.arrivalCity,
                    mission.arrivalState,
                  ) || "—"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <SoftBox
              display="flex"
              alignItems="center"
              gap={1.5}
              flex="1"
              minWidth={160}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={48}
                height={48}
                borderRadius="md"
                sx={(theme) => ({
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.common.white,
                })}
              >
                <Icon sx={{ fontSize: "1.75rem" }}>schedule</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                >
                  Proposed Date/Time
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" display="block">
                  {formatProposedDateTime(mission.dateTime) || "—"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {mission.legs?.length > 0 && (
            <>
              <SoftTypography
                variant="button"
                fontWeight="bold"
                color="dark"
                display="block"
                mb={1.5}
              >
                Flight Legs
              </SoftTypography>
              <TableContainer
                sx={(theme) => ({
                  borderRadius: "1rem",
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "auto",
                  width: "100%",
                })}
              >
                <Table
                  size="small"
                  sx={{
                    width: "100%",
                    minWidth: 600,
                    tableLayout: "fixed",
                    "& thead": {
                      width: "100%",
                      display: "table-header-group",
                    },
                    "& thead tr": { width: "100%", display: "table-row" },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "22%" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow
                      sx={(theme) => ({
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(99, 110, 255, 0.08)",
                      })}
                    >
                      <TableCell>Leg</TableCell>
                      <TableCell>Start Airport</TableCell>
                      <TableCell>End Airport</TableCell>
                      <TableCell>Proposed Date/Time</TableCell>
                      <TableCell>Pilot</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mission.legs.map((leg, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {getAirportName(leg.startAirport)}
                        </TableCell>
                        <TableCell>{getAirportName(leg.endAirport)}</TableCell>
                        <TableCell>
                          {formatProposedDateTime(
                            getLegProposedDateTime(leg, mission),
                          ) || "—"}
                        </TableCell>
                        <TableCell>{getPilotDisplay(leg)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </SoftBox>

        <Divider />

        {/* Pilot Requirements: MiniStatisticsCard (projects/general style) - single row */}
        <SoftBox pt={2} px={2} pb={2}>
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Pilot Requirements
          </SoftTypography>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{ fontWeight: "medium", text: "Commercial Rating" }}
                  count={
                    mission.pilotRequirements?.commercialRating === "Yes"
                      ? "Yes"
                      : "No"
                  }
                  icon={{ color: "info", component: "verified_user" }}
                  direction="left"
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{ fontWeight: "medium", text: "Flight Hour Minimums" }}
                  count={
                    mission.pilotRequirements?.flightHourMinimums || "None"
                  }
                  icon={{ color: "success", component: "schedule" }}
                  direction="left"
                  titleNoWrap
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{
                    fontWeight: "medium",
                    text: "Instrument Flight Rating",
                  }}
                  count={
                    mission.pilotRequirements?.instrumentFlightRating === "Yes"
                      ? "Yes"
                      : "No"
                  }
                  icon={{ color: "primary", component: "flight" }}
                  direction="left"
                  titleNoWrap
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{
                    fontWeight: "medium",
                    text: "Private Pilot's License",
                  }}
                  count={
                    mission.pilotRequirements?.privatePilotsLicense === "Yes"
                      ? "Yes"
                      : "No"
                  }
                  icon={{ color: "secondary", component: "badge" }}
                  direction="left"
                  titleNoWrap
                />
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>

        <Divider />

        {/* Cargo & Passengers: Card with table - full width */}
        <SoftBox pt={2} px={2} pb={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Cargo Items
              </SoftTypography>
              {!mission.cargoItems?.length ? (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  No cargo items
                </SoftTypography>
              ) : (
                <TableContainer
                  sx={(theme) => ({
                    borderRadius: "1rem",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "auto",
                    width: "100%",
                  })}
                >
                  <Table
                    size="small"
                    component="table"
                    sx={{
                      width: "100%",
                      minWidth: 600,
                      tableLayout: "fixed",
                      "& thead": {
                        width: "100%",
                        display: "table-header-group",
                      },
                      "& thead tr": { width: "100%", display: "table-row" },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "24%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "17%" }} />
                      <col style={{ width: "17%" }} />
                    </colgroup>
                    <TableHead>
                      <TableRow
                        sx={(theme) => ({
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(99, 110, 255, 0.08)",
                        })}
                      >
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Weight (lbs)</TableCell>
                        <TableCell align="right">Offered Value</TableCell>
                        <TableCell>Sourced</TableCell>
                        <TableCell>Provided By</TableCell>
                        <TableCell>Delivered to Airport</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mission.cargoItems.map((row, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{row.description}</TableCell>
                          <TableCell align="right">
                            {row.count != null && row.count !== ""
                              ? row.count
                              : "—"}
                          </TableCell>
                          <TableCell align="right">{row.weightLbs}</TableCell>
                          <TableCell align="right">
                            {row.offeredValue ?? "—"}
                          </TableCell>
                          <TableCell>{row.isSourced ? "Yes" : "No"}</TableCell>
                          <TableCell>{row.providedByOrgId || "—"}</TableCell>
                          <TableCell>
                            {row.isDeliveredToAirport || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
            <Grid item xs={12}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Passengers
              </SoftTypography>
              {!(mission.passengerItem || mission.PassengerItem)?.length ? (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  No passengers
                </SoftTypography>
              ) : (
                <TableContainer
                  sx={(theme) => ({
                    borderRadius: "1rem",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "auto",
                    width: "100%",
                  })}
                >
                  <Table
                    size="small"
                    component="table"
                    sx={{
                      width: "100%",
                      minWidth: 500,
                      tableLayout: "fixed",
                      "& thead": {
                        width: "100%",
                        display: "table-header-group",
                      },
                      "& thead tr": { width: "100%", display: "table-row" },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "25%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "20%" }} />
                    </colgroup>
                    <TableHead>
                      <TableRow
                        sx={(theme) => ({
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(99, 110, 255, 0.08)",
                        })}
                      >
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Age</TableCell>
                        <TableCell align="right">Weight (lbs)</TableCell>
                        <TableCell>Special Needs</TableCell>
                        <TableCell>Confirmed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(mission.passengerItem || mission.PassengerItem).map((row, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{row.fullName}</TableCell>
                          <TableCell align="right">{row.age}</TableCell>
                          <TableCell align="right">{row.weightLbs}</TableCell>
                          <TableCell>{row.specialNeeds || "—"}</TableCell>
                          <TableCell>{row.isConfirmed || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </SoftBox>

        <Divider />

        {/* Mission POC & Additional Info: billing-style */}
        <SoftBox pt={2} px={2} pb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Mission Point of Contact
              </SoftTypography>
              <SoftBox
                p={3}
                sx={(theme) => ({
                  borderRadius: "1rem",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(99, 110, 255, 0.06)",
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <SoftBox mb={1}>
                  <SoftTypography variant="caption" color="text">
                    Name:{" "}
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="medium">
                    {mission.missionPOCName || "—"}
                  </SoftTypography>
                </SoftBox>
                <SoftBox mb={1}>
                  <SoftTypography variant="caption" color="text">
                    Phone:{" "}
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="medium">
                    {mission.missionPOCPhone || "—"}
                  </SoftTypography>
                </SoftBox>
                <SoftBox>
                  <SoftTypography variant="caption" color="text">
                    Organization:{" "}
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="medium">
                    {pocOrgName || "—"}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Additional Information
              </SoftTypography>
              <SoftBox
                p={3}
                sx={(theme) => ({
                  borderRadius: "1rem",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(99, 110, 255, 0.06)",
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <SoftBox mb={1}>
                  <SoftTypography variant="caption" color="text">
                    Linked Request ID:{" "}
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="medium">
                    {mission.linkedRequestId || "—"}
                  </SoftTypography>
                </SoftBox>
                {mission.contributingOrgs?.length > 0 && (
                  <SoftBox mb={1}>
                    <SoftTypography
                      variant="caption"
                      color="text"
                      display="block"
                      mb={0.5}
                    >
                      Contributing Organizations
                    </SoftTypography>
                    <SoftBox display="flex" flexWrap="wrap" gap={1}>
                      {mission.contributingOrgs.map((org) => (
                        <SoftBadge
                          key={org}
                          variant="gradient"
                          color="secondary"
                          size="xs"
                          badgeContent={org}
                          container
                        />
                      ))}
                    </SoftBox>
                  </SoftBox>
                )}
                {mission.favoritedBy && (
                  <SoftBox>
                    <SoftTypography variant="caption" color="text">
                      Favorited By:{" "}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">
                      {mission.favoritedBy}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>

        <Divider />

        {/* Mission documents */}
        {!isGuest && (
          <SoftBox pt={2} px={2} pb={2}>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              <SoftTypography variant="h5" fontWeight="bold">
                Documents
              </SoftTypography>
              {!isViewOnlyUser && resolvedMissionId ? (
                <SoftButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => setDocumentDialogOpen(true)}
                >
                  Add Document
                </SoftButton>
              ) : null}
            </SoftBox>
            <SoftBox
              p={3}
              sx={(theme) => ({
                borderRadius: "1rem",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(99, 110, 255, 0.04)",
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              {documentsLoading ? (
                <SoftBox display="flex" alignItems="center" gap={1} py={1}>
                  <CircularProgress size={22} />
                  <SoftTypography variant="body2" color="text">
                    Loading documents…
                  </SoftTypography>
                </SoftBox>
              ) : documentsError ? (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  {documentsError}
                </Alert>
              ) : linkedDocuments.length === 0 ? (
                <SoftTypography variant="body2" color="text">
                  No documents linked yet. Upload a file to attach it to this
                  mission.
                </SoftTypography>
              ) : (
                <Grid container spacing={2}>
                  {linkedDocuments.map((doc, index) => {
                    const docKey =
                      doc.id ||
                      doc.timelineId ||
                      `${doc.documentPath}-${index}`;
                    const canAccess =
                      !isGuest &&
                      user?.id_token &&
                      (doc.key || doc.documentPath);
                    const isBusy = documentActionId === docKey;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={docKey}>
                        <DocumentCard
                          doc={doc}
                          canAccess={canAccess}
                          isBusy={isBusy}
                          onView={handleViewDocument}
                          onDownload={handleDownloadDocument}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </SoftBox>
            <MissionDocumentViewerDialog
              open={!!viewerDocument}
              onClose={() => setViewerDocument(null)}
              document={viewerDocument}
              idToken={user?.id_token}
            />
            <MissionDocumentUploadDialog
              open={documentDialogOpen}
              onClose={() => setDocumentDialogOpen(false)}
              missionId={resolvedMissionId}
              idToken={user?.id_token}
              onSuccess={() => {
                fetchMissionDocuments();
                fetchTimeline();
                window.dispatchEvent(
                  new CustomEvent("globalNotification", {
                    detail: {
                      type: "success",
                      title: "Document linked",
                      message:
                        "Documents were uploaded and linked to this mission.",
                    },
                  }),
                );
              }}
            />
          </SoftBox>
        )}

        <Divider />

        {/* Mission Timeline */}
        <SoftBox pt={2} px={2} pb={3}>
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Mission Timeline
          </SoftTypography>
          <SoftBox
            p={3}
            sx={(theme) => ({
              borderRadius: "1rem",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(99, 110, 255, 0.04)",
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            {timelineLoading ? (
              <SoftBox display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={22} />
                <SoftTypography variant="body2" color="text">
                  Loading timeline…
                </SoftTypography>
              </SoftBox>
            ) : timeline.length === 0 ? (
              <SoftTypography variant="body2" color="text">
                No timeline events yet.
              </SoftTypography>
            ) : (
              timeline.map((event, index) => (
              <SoftBox
                key={event.timelineId || `${event.title}-${index}`}
                display="flex"
                gap={1.5}
              >
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  pt={0.25}
                  minWidth={20}
                >
                  <SoftBadge
                    color={event.color}
                    variant="gradient"
                    badgeContent=""
                    circular
                    size="xs"
                    container
                  />
                  {index < timeline.length - 1 && (
                    <SoftBox
                      sx={(theme) => ({
                        width: "2px",
                        flex: 1,
                        backgroundColor: theme.palette.divider,
                        my: 0.75,
                        minHeight: 26,
                      })}
                    />
                  )}
                </SoftBox>
                <SoftBox pb={index < timeline.length - 1 ? 1.5 : 0}>
                  <SoftBox display="flex" alignItems="center" gap={0.75}>
                    <Icon fontSize="small" color={event.color}>
                      {event.icon}
                    </Icon>
                    <SoftTypography
                      variant="button"
                      fontWeight="bold"
                      color="dark"
                    >
                      {event.title}
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="body2" color="text" mt={0.25}>
                    {event.detail}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
                    {event.meta}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ))
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Dialog
        open={pendingDeleteMission}
        onClose={() => !deletingMission && setPendingDeleteMission(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete mission?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{mission?.title || "this mission"}"? This will mark the
            mission as inactive and remove it from the list.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            onClick={() => setPendingDeleteMission(false)}
            disabled={deletingMission}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            onClick={handleDeleteMission}
            disabled={deletingMission}
          >
            {deletingMission ? "Deleting…" : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default MissionDetailsPage;
